import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "../../lib/api";

type Recipient = {
  id: number;
  contact_id: number;
  channel: string;
  status: string;
  error: string | null;
  sent_at: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
};

type Message = {
  id: number;
  title: string;
  body: string;
  channels: string;
  target_type: string;
  target_data: string | null;
  attachment_r2_key: string | null;
  created_at: string;
  dispatched_at: string | null;
  scheduled_at: string | null;
};

type DetailResponse = {
  message: Message;
  recipients: Recipient[];
};

const statusLabel: Record<string, string> = {
  pending: "Čeká",
  sent: "Odesláno",
  received: "Doručeno",
  failed: "Chyba",
};

const channelLabel: Record<string, string> = {
  push: "Aplikace",
  sms: "SMS",
  email: "Email",
};

export default function AdminMessageDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ["message", id],
    queryFn: () => apiGet<DetailResponse>(`/api/admin/messages/${id}`),
  });

  if (isLoading) return <p>Načítám…</p>;
  if (!data) return <p>Zpráva nenalezena.</p>;

  const { message: msg, recipients } = data;
  const channels: string[] = JSON.parse(msg.channels);

  const grouped: Record<string, Recipient[]> = {};
  for (const r of recipients) {
    (grouped[r.channel] ??= []).push(r);
  }

  return (
    <div>
      <Link to="/admin/messages">← Zpět na zprávy</Link>

      <h1>{msg.title}</h1>

      <table>
        <tbody>
          <tr>
            <th style={{ textAlign: "left", paddingRight: 16 }}>Cíl</th>
            <td>{{ all: "Všichni", polygon: "Polygon", list: "Distribuční seznam", streets: "Ulice" }[msg.target_type] ?? msg.target_type}</td>
          </tr>
          <tr>
            <th style={{ textAlign: "left", paddingRight: 16 }}>Kanály</th>
            <td>{channels.map((ch) => channelLabel[ch] ?? ch).join(", ")}</td>
          </tr>
          <tr>
            <th style={{ textAlign: "left", paddingRight: 16 }}>Vytvořeno</th>
            <td>{new Date(msg.created_at).toLocaleString("cs-CZ")}</td>
          </tr>
          {msg.scheduled_at && (
            <tr>
              <th style={{ textAlign: "left", paddingRight: 16 }}>Naplánováno</th>
              <td>{new Date(msg.scheduled_at).toLocaleString("cs-CZ")}</td>
            </tr>
          )}
          <tr>
            <th style={{ textAlign: "left", paddingRight: 16 }}>Odesláno</th>
            <td>
              {msg.dispatched_at
                ? new Date(msg.dispatched_at).toLocaleString("cs-CZ")
                : msg.scheduled_at
                  ? "Čeká na naplánovaný čas"
                  : "Čeká"}
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Text zprávy</h3>
      <p style={{ whiteSpace: "pre-wrap", background: "#f5f5f5", padding: 12, borderRadius: 4 }}>
        {msg.body}
      </p>

      {channels.map((ch) => {
        const rows = grouped[ch] ?? [];
        return (
          <div key={ch} style={{ marginTop: 24 }}>
            <h3>
              {channelLabel[ch] ?? ch} ({rows.length})
            </h3>
            {rows.length === 0 ? (
              <p className="muted">Žádní příjemci</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Jméno</th>
                    {ch === "email" && <th>Email</th>}
                    {ch === "sms" && <th>Telefon</th>}
                    <th>Stav</th>
                    <th>Odesláno</th>
                    {ch !== "push" || undefined}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.first_name} {r.last_name}
                      </td>
                      {ch === "email" && <td className="muted">{r.email}</td>}
                      {ch === "sms" && <td className="muted">{r.phone}</td>}
                      <td>
                        <span
                          style={{
                            color:
                              r.status === "failed"
                                ? "#c00"
                                : r.status === "received"
                                  ? "#070"
                                  : r.status === "sent"
                                    ? "#060"
                                    : undefined,
                          }}
                        >
                          {statusLabel[r.status] ?? r.status}
                        </span>
                        {r.error && (
                          <span className="muted" style={{ marginLeft: 8 }}>
                            ({r.error})
                          </span>
                        )}
                      </td>
                      <td className="muted">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString("cs-CZ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
